import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import {
  isDeliveredItemInReturn,
  isReturnedOrderItem,
  OrderItemTrackingStatusEnum,
  OrderLineItem,
  OrderLineItemAdminPanel,
  usdToPoints,
} from '@models';
import { CommonModule } from '@angular/common';
import {
  DigitsGroupingPipe,
  ImageFallbackDirective,
  NoticePanelComponent,
  PartialHiddenCreditCardComponent,
  PriceAndPointsComponent,
} from '@shared';
import { AppleCareListingComponent } from '../../../../../ui/apple-components/apple-care-listing/apple-care-listing.component';
import { AppleCareItem, ConfigurationService, UserService } from '@core';

import { PAID_TITLE_BY_STATUS, TOTAL_LABEL_BY_STATUS } from './order-item.constant';

@Component({
  selector: 'lib-order-item',
  templateUrl: './order-item.component.html',
  styleUrl: './order-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PartialHiddenCreditCardComponent,
    NoticePanelComponent,
    DigitsGroupingPipe,
    PriceAndPointsComponent,
    ImageFallbackDirective,
    AppleCareListingComponent,
  ],
})
export class OrderItemComponent {
  protected readonly rewardName = inject(ConfigurationService).rewardName;
  item = input.required<OrderLineItem | OrderLineItemAdminPanel | any>();
  lastFourDigits = input<string>();

  appleCareItem = input<AppleCareItem | undefined>();
  isEngravable = input<boolean>(false);
  /** Admin-panel keeps US dollars; the storefront shows Shopping Dollars only (US-02.2 AC12). */
  showUsd = input<boolean>(false);
  engravingText = input<string | null>(null);
  hasEngraving = input<boolean>(false);

  readonly detailInfo = 'Returned';
  readonly refundInfo = computed(
    () =>
      `You'll receive a refund for all ${this.rewardName()} you used on your order. Any remaining amount will be credited back to your card.`,
  );

  itemNewPoints = computed(() => {
    const item = this.item();
    return usdToPoints(item?.price || 0);
  });

  // Determine payment method for AppleCare
  shouldAddAppleCareToPrice = computed(() => {
    const baseSubtotalPrice = this.item()?.total?.subtotalPrice || 0;
    const baseSubtotalPoints = this.item()?.total?.subtotalPoints || 0;

    // If only dollars were used, add to price
    if (baseSubtotalPrice > 0 && baseSubtotalPoints === 0) {
      return true;
    }

    // If only points were used, don't add to price
    if (baseSubtotalPoints > 0 && baseSubtotalPrice === 0) {
      return false;
    }

    // For hybrid payments, we need a rule. Let's prioritize dollars
    // (you could also prioritize points or use another business rule)
    return baseSubtotalPrice > 0;
  });

  shouldAddAppleCareToPoints = computed(() => {
    const baseSubtotalPrice = this.item()?.total?.subtotalPrice || 0;
    const baseSubtotalPoints = this.item()?.total?.subtotalPoints || 0;

    // If only points were used, add to points
    if (baseSubtotalPoints > 0 && baseSubtotalPrice === 0) {
      return true;
    }

    // If only dollars were used, don't add to points
    if (baseSubtotalPrice > 0 && baseSubtotalPoints === 0) {
      return false;
    }

    // For hybrid payments, only add to points if we're NOT adding to price
    return !this.shouldAddAppleCareToPrice();
  });

  // Computed properties for totals that include AppleCare
  combinedSubtotalPrice = computed(() => {
    const baseSubtotal = this.item()?.total?.subtotalPrice || 0;
    const appleCareItem = this.appleCareItem();

    if (!appleCareItem) {
      return baseSubtotal;
    }

    // Add AppleCare's actual subtotal price paid (backend has already split it proportionally)
    return baseSubtotal + (appleCareItem.subtotalPrice || 0);
  });

  combinedSubtotalPoints = computed(() => {
    const baseSubtotalPoints = this.item()?.total?.subtotalPoints || 0;
    const appleCareItem = this.appleCareItem();

    if (!appleCareItem) {
      // Round subtotal points for consistent display
      return Math.floor(baseSubtotalPoints);
    }

    // Round each component separately so displayed item points add up correctly
    const baseRounded = Math.floor(baseSubtotalPoints);
    const appleCareRounded = Math.floor(appleCareItem.subtotalPoints || 0);
    return baseRounded + appleCareRounded;
  });

  combinedTotalPrice = computed(() => {
    const item = this.item();
    const totalPrice = item?.total?.totalPrice || 0;
    const appleCareItem = this.appleCareItem();

    // If no AppleCare, use backend's total directly
    if (!appleCareItem) {
      return totalPrice;
    }

    // Add AppleCare's actual payment amount - backend has already split it proportionally
    return totalPrice + (appleCareItem.subtotalPrice || 0);
  });

  combinedTotalPoints = computed(() => {
    const item = this.item();
    const totalPoints = item?.total?.totalPoints || 0;
    const appleCareItem = this.appleCareItem();

    // If no AppleCare, use backend's total directly
    if (!appleCareItem) {
      return Math.floor(totalPoints);
    }

    const totalRounded = Math.floor(totalPoints);
    const appleCareRounded = Math.floor(appleCareItem.subtotalPoints || 0);
    return totalRounded + appleCareRounded;
  });

  paidTitle = computed<string>(() => PAID_TITLE_BY_STATUS[(this.item() as OrderLineItem).status]);

  showRefundInfo = computed<boolean>(() =>
    Boolean(
      this.isReturnedStatus() &&
      (this.item() as any)?.refund.totalAmountRefunded &&
      (this.item() as any)?.refund.pointsCredited,
    ),
  );

  isRefundPrices = computed<boolean>(() =>
    [OrderItemTrackingStatusEnum.Returned, OrderItemTrackingStatusEnum.Canceled].includes(this.item().status),
  );

  totalLabel = computed<string>(() => TOTAL_LABEL_BY_STATUS[(this.item() as OrderLineItem).status]);

  isReturnedStatus = computed(() => isReturnedOrderItem(this.item().status));
  isDeliveredItemInReturn = computed(() => isDeliveredItemInReturn(this.item()));

  userService = inject(UserService);
}
